"use client";
import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { FaCheck, FaArrowRight } from "react-icons/fa";
import {
  FileSearch,
  PencilRuler,
  Palette,
  Send,
  ShieldCheck,
  Tag,
  CalendarClock,
  Users,
} from "lucide-react";
import { ButtonCta, LiveChatButton } from "@/components";
import { fontSizes } from "@/constants/constants";

/* ------------------------------- data ------------------------------- */

const packageIncludes = [
  "Coordinated support from editing through distribution setup",
  "Professional formatting and layout for print and digital editions",
  "Cover and interior design suited to your book's genre",
  "Regular updates and a clear point of contact throughout",
  "Guidance on building an author presence online",
  "Print and eBook preparation, with broader distribution options at higher tiers",
];

const editingCovers = [
  "Structural and developmental review",
  "Grammar and syntax refinement",
  "Tone and consistency checks",
  "Readability adjustments for both print and digital formats",
];

const workflow = [
  {
    icon: FileSearch,
    title: "Manuscript Review",
    description:
      "We review your manuscript and discuss your goals to put together a workable plan.",
  },
  {
    icon: PencilRuler,
    title: "Editing & Proofreading",
    description:
      "Your manuscript is refined for clarity, accuracy, and tone.",
  },
  {
    icon: Palette,
    title: "Formatting & Cover Design",
    description:
      "We format your book to general publishing standards and design a cover suited to its genre and audience.",
  },
  {
    icon: Send,
    title: "Distribution Setup",
    description:
      "Your book is prepared in print and eBook formats and set up for distribution through the retailers and platforms included in your package.",
  },
];

const packages = [
  {
    name: "Silver",
    tagline:
      "Suited to a shorter manuscript and a more straightforward path to print",
    featured: false,
    groups: [
      {
        title: "Writing Support",
        items: [
          "A dedicated project manager for your project",
          "Full manuscript writing support (roughly 50,000–70,000 words)",
          "Two rounds of revisions",
          "Basic research support",
          "Chapter summaries and an outline",
          "A complimentary consultation on positioning and genre strategy",
          "Guidance on eBook formatting for self-distribution",
        ],
      },
      {
        title: "Editing",
        items: [
          "Professional copyediting — grammar, clarity, and consistency",
          "Sentence-level refinement for readability",
          "A consistency check across names, timelines, and formatting",
          "Basic formatting alignment for publication",
          "Two revision passes and a final proofread",
        ],
      },
      {
        title: "Publishing Preparation",
        items: [
          "Manuscript review and consultation",
          "Proofreading and quality assurance",
          "Typesetting for print and digital formats",
          "Basic formatting and style consistency",
          "Up to two personalized draft revisions per chapter",
          "Cover design with front, back, and spine layout",
          "ISBN registration and barcode assignment",
          "Distribution account setup and basic optimization",
          "eBook format conversion and paperback preparation",
        ],
      },
    ],
  },
  {
    name: "Gold",
    tagline:
      "Suited to a fuller manuscript and a more complete production process",
    featured: true,
    badge: "Most Requested",
    groups: [
      {
        title: "Writing Support",
        items: [
          "A dedicated project manager for your project",
          "Manuscript writing in a professional tone, matched to your voice (roughly 70,000–120,000 words)",
          "Three to four rounds of revisions",
          "Research support with references",
          "Outline and synopsis creation",
          "Extensive research and expert interviews where relevant",
          "Storyboarding, chapter structuring, and character arc support",
        ],
      },
      {
        title: "Editing",
        items: [
          "A light developmental review covering structure and flow",
          "Full line editing for sentence-level improvement and tone",
          "Copyediting for grammar, consistency, and clarity",
          "A style guide created for your manuscript",
          "Five revision cycles and a final proofread",
          "An editorial summary report with improvement notes",
        ],
      },
      {
        title: "Publishing Preparation",
        items: [
          "Comprehensive manuscript review and consultation",
          "Custom layout adjustments for readability and visual appeal",
          "Up to five personalized draft revisions per chapter",
          "A custom or illustrated cover design, with premium layout",
          "Custom typography and visual branding guidance",
          "ISBN registration and barcode assignment",
          "Distribution setup across a broader set of platforms",
          "eBook format conversion and paperback preparation",
          "Metadata review to support discoverability",
        ],
      },
    ],
  },
  {
    name: "Platinum",
    tagline:
      "Suited to a longer manuscript and a fuller-scope publishing process",
    featured: false,
    groups: [
      {
        title: "Writing Support",
        items: [
          "A dedicated project manager working in an author-coach style",
          "Collaborative manuscript creation with regular author check-ins (roughly 120,000–200,000 words)",
          "Full research and content verification",
          "Detailed story arc, character development, and pacing support",
          "Revisions included within the agreed project scope",
          "A custom cover concept consultation",
          "A personal marketing consultation call",
          "A personalized launch-window checklist",
          "Up to 40 images included",
        ],
      },
      {
        title: "Editing",
        items: [
          "Full developmental editing, including big-picture restructuring",
          "Chapter-by-chapter feedback",
          "Story arc or logical-flow reconstruction",
          "In-depth line editing and copyediting",
          "Tone and voice strengthening throughout",
          "Revisions included within the agreed project scope",
          "An editorial strategy consultation call",
          "A publishing-readiness review and final proofread after layout",
        ],
      },
      {
        title: "Publishing Preparation",
        items: [
          "Comprehensive manuscript review and consultation",
          "Custom layout adjustments and full formatting consistency",
          "Revisions included within the agreed project scope, per chapter",
          "A bespoke or illustrated cover design, front, back, and spine",
          "Custom typography and a visual branding consultation",
          "ISBN registration and barcode assignment",
          "Broader distribution setup, including international library and bookstore guidance where applicable",
          "eBook format conversion across multiple formats, plus paperback and hardcover preparation",
          "Metadata review aimed at supporting discoverability",
          "Optional pre-order launch assistance",
        ],
      },
    ],
  },
];

const genres = [
  "Romance",
  "Science Fiction",
  "Historical Fiction",
  "Mystery & Thriller",
  "Self-Help",
  "Fantasy",
];

const priorities = [
  {
    icon: ShieldCheck,
    title: "Author Ownership",
    description:
      "You retain ownership and rights to your book, as set out clearly in your service agreement.",
  },
  {
    icon: Tag,
    title: "Transparent Pricing",
    description:
      "Pricing is presented upfront, with package details laid out clearly before you commit to anything.",
  },
  {
    icon: CalendarClock,
    title: "Defined Timelines",
    description:
      "Each project follows a structured workflow with a timeline communicated at the outset.",
  },
  {
    icon: Users,
    title: "Collaborative Process",
    description:
      "Your input shapes decisions throughout — covers, formatting choices, and editorial direction are made with you, not for you.",
  },
];

/* ---------------------------- small pieces --------------------------- */

const CheckItem = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold/10 text-gold-deep">
      <FaCheck className="text-[10px]" />
    </span>
    <span className="text-[#3D3D3D] text-sm leading-relaxed">{children}</span>
  </li>
);

const SectionHeading = ({ eyebrow, title, children, light = false }) => (
  <div className="max-w-3xl mx-auto text-center mb-10" data-aos="fade-up">
    {eyebrow && (
      <span
        className={`inline-block mb-4 rounded-full px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] ${
          light
            ? "bg-white/15 text-white"
            : "bg-gold/10 text-gold-deep"
        }`}
      >
        {eyebrow}
      </span>
    )}
    <h2
      className={`font-display font-semibold ${fontSizes.text_4_xl} ${
        light ? "text-white" : "text-gray-900"
      }`}
    >
      {title}
    </h2>
    {children && (
      <p
        className={`mt-4 ${fontSizes.base} ${
          light ? "text-white/80" : "text-[#5F5F5F]"
        }`}
      >
        {children}
      </p>
    )}
  </div>
);

/* ------------------------------- page -------------------------------- */

const PricingContent = () => {
  useEffect(() => {
    AOS.init({ duration: 800, once: true, easing: "ease-in-out" });
  }, []);

  return (
    <div className="overflow-hidden">
      {/* local animations */}
      <style jsx global>{`
        @keyframes pp-float {
          0%,
          100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-24px) scale(1.06);
          }
        }
        .pp-blob {
          animation: pp-float 9s ease-in-out infinite;
        }
      `}</style>

      {/* ============================== HERO ============================== */}
      <section className="relative isolate flex items-center justify-center overflow-hidden bg-gradient-to-br from-primary via-[#3BB4E0] to-secondary px-5 py-24 md:py-32">
        <div className="pp-blob absolute -top-24 -left-16 h-72 w-72 rounded-full bg-white/15 blur-3xl" />
        <div
          className="pp-blob absolute -bottom-24 -right-10 h-80 w-80 rounded-full bg-white/10 blur-3xl"
          style={{ animationDelay: "2s" }}
        />
        <div className="relative z-10 max-w-3xl text-center text-white">
          <span
            className="inline-block mb-5 rounded-full bg-white/15 px-5 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] backdrop-blur-sm"
            data-aos="fade-down"
          >
            Pricing Plans
          </span>
          <h1
            className={`font-display font-semibold leading-tight ${fontSizes.text_6_xl}`}
            data-aos="fade-up"
          >
            Clear Packages for Every Stage of Publishing
          </h1>
          <p
            className={`mx-auto mt-6 max-w-2xl text-white/85 ${fontSizes.base}`}
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Whether this is a first manuscript or a more involved project, our
            packages are built to scale with what your book actually needs.
            Pricing is presented upfront, with the specifics of each tier laid
            out below — no hidden steps added once you've started.
          </p>
          <div
            className="mt-9 flex justify-center"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            <LiveChatButton
              buttonText="Ask a Question"
              classStyle="text-primary bg-white hover:bg-white/90 h-12"
            />
          </div>
        </div>
      </section>

      {/* ===================== WHAT WORKING LOOKS LIKE ==================== */}
      <section className="bg-white px-5 py-16 md:py-24">
        <div className="max-w-[1350px] mx-auto grid gap-12 lg:grid-cols-2 lg:items-center">
          <div data-aos="fade-right">
            <span className="inline-block mb-4 rounded-full bg-secondary/15 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              How It Works
            </span>
            <h2 className={`font-display font-semibold text-gray-900 ${fontSizes.text_4_xl}`}>
              What Working With Us Looks Like
            </h2>
            <p className={`mt-5 text-[#5F5F5F] ${fontSizes.base}`}>
              Turning a manuscript into a finished book involves a number of
              coordinated steps. We aim to keep that process organized and
              predictable, with clear communication at each stage rather than
              long gaps between updates.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <LiveChatButton
                buttonText="Ask a Question"
                classStyle="text-[#fff] bg-primary hover:bg-primary/90 h-12"
              />
              <ButtonCta
                buttonText="Get Started"
                classStyle="text-[#fff] bg-secondary hover:bg-secondary/90 h-12"
              />
            </div>
          </div>

          <div
            className="rounded-3xl border border-[#E4E4E4] bg-[#F7F9FB] p-8 shadow-sm"
            data-aos="fade-left"
          >
            <h3 className="font-display mb-6 text-xl font-semibold text-gray-900">
              What's Included Across Our Packages
            </h3>
            <ul className="space-y-4">
              {packageIncludes.map((item, i) => (
                <CheckItem key={i}>{item}</CheckItem>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ======================= EDITING INCLUDED ======================== */}
      <section className="bg-[#F0F0F0] px-5 py-16 md:py-24">
        <div className="max-w-[1350px] mx-auto grid gap-12 lg:grid-cols-2 lg:items-center">
          <div
            className="order-2 lg:order-1 rounded-3xl border border-[#E4E4E4] bg-white p-8 shadow-sm"
            data-aos="fade-right"
          >
            <h3 className="font-display mb-6 text-xl font-semibold text-gray-900">
              What Our Editing Covers
            </h3>
            <ul className="grid gap-4 sm:grid-cols-2">
              {editingCovers.map((item, i) => (
                <CheckItem key={i}>{item}</CheckItem>
              ))}
            </ul>
          </div>
          <div className="order-1 lg:order-2" data-aos="fade-left">
            <span className="inline-block mb-4 rounded-full bg-secondary/15 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              Always Included
            </span>
            <h2 className={`font-display font-semibold text-gray-900 ${fontSizes.text_4_xl}`}>
              Editing Included in Every Package
            </h2>
            <p className={`mt-5 text-[#5F5F5F] ${fontSizes.base}`}>
              Manuscripts are reviewed against general publishing-readiness
              standards, with attention to clarity, flow, and consistency while
              preserving the author's voice.
            </p>
            <div className="mt-8">
              <LiveChatButton
                buttonText="Ask a Question"
                classStyle="text-[#fff] bg-primary hover:bg-primary/90 h-12"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ====================== HOW A PROJECT MOVES ====================== */}
      <section className="bg-white px-5 py-16 md:py-24">
        <div className="max-w-[1350px] mx-auto">
          <SectionHeading eyebrow="The Process" title="How a Project Moves Forward" />
          <div className="relative grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {/* connecting line (desktop) */}
            <div className="pointer-events-none absolute left-0 right-0 top-9 hidden h-px bg-gradient-to-r from-transparent via-secondary to-transparent lg:block" />
            {workflow.map((step, i) => {
              const Icon = step.icon;
              return (
                <div
                  key={i}
                  className="group relative rounded-2xl border border-[#F1F8FC] bg-white p-6 text-center shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-primary hover:shadow-xl"
                  data-aos="fade-up"
                  data-aos-delay={i * 100}
                >
                  <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-secondary text-white shadow-lg transition-transform duration-300 group-hover:scale-110">
                    <Icon className="h-7 w-7" />
                  </div>
                  <span className="font-display text-sm font-semibold text-secondary">
                    Step {i + 1}
                  </span>
                  <h3 className="font-display mt-1 text-lg font-semibold text-gray-900">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#5F5F5F]">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========================= PACKAGE DETAILS ======================= */}
      <section className="bg-gradient-to-b from-[#F0F0F0] to-white px-5 py-16 md:py-24">
        <div className="max-w-[1350px] mx-auto">
          <SectionHeading eyebrow="Package Details" title="Choose the Package That Fits Your Book">
            Every tier includes editing, formatting, cover design, and
            distribution setup — the scope grows with the size and ambition of
            your project.
          </SectionHeading>

          <div className="grid items-start gap-8 lg:grid-cols-3">
            {packages.map((pkg, i) => (
              <div
                key={pkg.name}
                data-aos="fade-up"
                data-aos-delay={i * 120}
                className={`relative flex h-full flex-col rounded-3xl bg-white p-8 transition-all duration-300 hover:-translate-y-2 ${
                  pkg.featured
                    ? "border-2 border-primary shadow-2xl lg:-mt-4 lg:mb-4"
                    : "border border-[#E4E4E4] shadow-sm hover:shadow-xl"
                }`}
              >
                {pkg.badge && (
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-primary to-secondary px-5 py-1.5 text-xs font-semibold uppercase tracking-wider text-white shadow-lg">
                    {pkg.badge}
                  </span>
                )}

                {/* header */}
                <div className="border-b border-dashed border-[#E4E4E4] pb-6 text-center">
                  <h3 className="font-display text-3xl font-semibold text-gray-900">
                    {pkg.name}
                  </h3>
                  <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-[#5F5F5F]">
                    {pkg.tagline}
                  </p>
                  <p className="mt-5 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                    Custom Quote
                  </p>
                </div>

                {/* feature groups */}
                <div className="mt-6 flex-1 space-y-7">
                  {pkg.groups.map((group) => (
                    <div key={group.title}>
                      <h4 className="mb-4 flex items-center gap-3 text-sm font-semibold uppercase tracking-wide text-gray-900">
                        <span className="h-2 w-2 rounded-full bg-secondary" />
                        {group.title}
                      </h4>
                      <ul className="space-y-3">
                        {group.items.map((item, idx) => (
                          <CheckItem key={idx}>{item}</CheckItem>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {/* cta */}
                <div className="mt-8">
                  <ButtonCta
                    buttonText="Get a Quote"
                    classStyle={`w-full justify-center h-12 text-[#fff] ${
                      pkg.featured
                        ? "bg-gradient-to-r from-primary to-secondary hover:opacity-90"
                        : "bg-primary hover:bg-primary/90"
                    }`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= GENRES SECTION ======================== */}
      <section className="bg-[#000000] px-5 py-16 md:py-24">
        <div className="max-w-[1350px] mx-auto">
          <SectionHeading eyebrow="Genre Range" title="Genres We Work Across" light>
            We work across a range of categories — including romance, science
            fiction, historical fiction, mystery and thriller, self-help, and
            fantasy — with each project handled by people familiar with that
            genre's conventions.
          </SectionHeading>

          <div
            className="mb-10 flex flex-wrap justify-center gap-3"
            data-aos="fade-up"
          >
            {genres.map((g) => (
              <span
                key={g}
                className={`rounded-full border px-5 py-2 text-sm font-medium transition-all duration-300 ${
                  g === "Romance"
                    ? "border-primary bg-primary text-white"
                    : "border-white/20 text-white/80 hover:border-secondary hover:text-white"
                }`}
              >
                {g}
              </span>
            ))}
          </div>

          <div
            className="mx-auto max-w-2xl rounded-3xl border border-white/10 bg-white/5 p-8 text-center backdrop-blur-sm"
            data-aos="zoom-in"
          >
            <h3 className="font-display text-2xl font-semibold text-white">Romance</h3>
            <p className="mt-3 text-white/75 text-sm leading-relaxed">
              Stories built around emotional connection, formatted and designed
              with the genre's readers in mind.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-4">
              <LiveChatButton
                buttonText="Ask a Question"
                classStyle="text-[#000000] bg-white hover:bg-white/90 h-12"
              />
              <ButtonCta
                buttonText="Get Started"
                classStyle="text-[#fff] bg-secondary hover:bg-secondary/90 h-12"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ======================== WHAT WE PRIORITIZE ===================== */}
      <section className="bg-white px-5 py-16 md:py-24">
        <div className="max-w-[1350px] mx-auto">
          <SectionHeading eyebrow="Our Commitments" title="What We Prioritize">
            We aim to provide publishing, editing, and design support that helps
            authors produce a well-prepared, market-ready book, with the work
            handled by people who take the craft seriously.
          </SectionHeading>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {priorities.map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="group rounded-2xl border border-[#F1F8FC] bg-[#F7F9FB] p-7 transition-all duration-300 hover:-translate-y-2 hover:bg-primary hover:shadow-xl"
                  data-aos="fade-up"
                  data-aos-delay={i * 100}
                >
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-xl bg-primary text-white transition-colors duration-300 group-hover:bg-white group-hover:text-primary">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-display mb-2 text-lg font-semibold text-gray-900 transition-colors duration-300 group-hover:text-white">
                    {item.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-[#5F5F5F] transition-colors duration-300 group-hover:text-white/90">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div
            className="mt-12 flex flex-wrap justify-center gap-4"
            data-aos="fade-up"
          >
            <LiveChatButton
              buttonText="Ask a Question"
              classStyle="text-[#fff] bg-primary hover:bg-primary/90 h-12"
            />
            <ButtonCta
              buttonText="Get Started"
              classStyle="text-[#fff] bg-secondary hover:bg-secondary/90 h-12"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingContent;
